'use client'

/* eslint-disable @next/next/no-img-element */

import React, { useState } from 'react'

import type { Header as HeaderType, HeaderNavItemLink } from '@/Header/types'

import Link from 'next/link'
import { SearchIcon, ChevronRight, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/utilities/ui'

interface MobileMenuProps {
  data: HeaderType
  logo?: {
    url?: string | null
    alt?: string | null
    width?: number | null
    height?: number | null
  }
  isOpen: boolean
  onClose: () => void
}

const resolveNavItemHref = (item: HeaderNavItemLink): string => {
  if (
    item.linkType === 'reference' &&
    item.reference?.relationTo &&
    item.reference.value &&
    typeof item.reference.value === 'object' &&
    'slug' in item.reference.value &&
    typeof item.reference.value.slug === 'string'
  ) {
    const relationTo = item.reference.relationTo
    const slug = item.reference.value.slug

    return relationTo !== 'pages' ? `/${relationTo}/${slug}` : `/${slug}`
  }

  return item.url || '#'
}

export const MobileMenu: React.FC<MobileMenuProps> = ({ data, logo, isOpen, onClose }) => {
  const [expandedDropdowns, setExpandedDropdowns] = useState<Set<number>>(new Set())

  const navItems = data?.navItems || []

  const toggleDropdown = (index: number) => {
    setExpandedDropdowns((prev) => {
      const next = new Set(prev)
      if (next.has(index)) {
        next.delete(index)
      } else {
        next.add(index)
      }
      return next
    })
  }

  if (!isOpen) return null

  return (
    <div className="lg:hidden">
      <div
        className="fixed inset-0 z-40 bg-brand-black/20 backdrop-blur-sm"
        aria-hidden="true"
        onClick={onClose}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            onClose()
          }
        }}
      />
      <div
        className={cn(
          'fixed inset-y-0 right-0 z-50 w-full max-w-sm bg-background shadow-2xl',
          'animate-in slide-in-from-right duration-300',
        )}
        role="dialog"
        aria-modal="true"
        aria-label="Mobile navigation"
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            onClose()
          }
        }}
      >
        <div className="flex h-full flex-col">
          <div className="flex items-center justify-between border-b border-border px-4 py-4">
            <Link href="/" onClick={onClose}>
              {logo?.url && (
                <img
                  src={logo.url}
                  alt={logo.alt || 'Tarcontent Developers Limited'}
                  width={logo.width || 250}
                  height={logo.height || 40}
                  className="h-8 w-auto"
                />
              )}
            </Link>
            <button
              onClick={onClose}
              className="flex h-8 w-8 items-center justify-center rounded-md text-foreground/80 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-red focus-visible:ring-offset-2"
              aria-label="Close menu"
            >
              <X className="h-5 w-5" aria-hidden="true" />
            </button>
          </div>

          <nav className="flex-1 overflow-y-auto px-4 py-6" aria-label="Mobile navigation">
            <ul className="space-y-1">
              {navItems.map((item, index) => {
                if (item.type === 'dropdown') {
                  const isExpanded = expandedDropdowns.has(index)
                  return (
                    <li key={item.id || index}>
                      <button
                        onClick={() => toggleDropdown(index)}
                        className={cn(
                          'flex w-full items-center justify-between rounded-md px-3 py-3 text-base font-medium',
                          'text-foreground/80 hover:bg-accent hover:text-foreground',
                          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-red focus-visible:ring-offset-2',
                        )}
                        aria-expanded={isExpanded}
                      >
                        {item.dropdownTitle}
                        <ChevronRight
                          className={cn(
                            'h-5 w-5 transition-transform duration-200',
                            isExpanded && 'rotate-90',
                          )}
                          aria-hidden="true"
                        />
                      </button>
                      {isExpanded && (
                        <ul className="ml-4 mt-1 space-y-1 border-l border-border pl-4">
                          {item.dropdownLinks?.map((link, linkIndex) => (
                            <li key={linkIndex}>
                              <Link
                                href={link.url}
                                className={cn(
                                  'block rounded-md px-3 py-2 text-sm',
                                  'text-foreground/70 hover:bg-accent hover:text-foreground',
                                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-red focus-visible:ring-offset-2',
                                )}
                                target={link.newTab ? '_blank' : undefined}
                                rel={link.newTab ? 'noopener noreferrer' : undefined}
                                onClick={onClose}
                              >
                                {link.label}
                                {link.description && (
                                  <span className="block text-xs text-muted-foreground">
                                    {link.description}
                                  </span>
                                )}
                              </Link>
                            </li>
                          ))}
                        </ul>
                      )}
                    </li>
                  )
                }

                return (
                  <li key={item.id || index}>
                    <Link
                      href={resolveNavItemHref(item)}
                      className={cn(
                        'block rounded-md px-3 py-3 text-base font-medium',
                        'text-foreground/80 hover:bg-accent hover:text-foreground',
                        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-red focus-visible:ring-offset-2',
                      )}
                      target={item.newTab ? '_blank' : undefined}
                      rel={item.newTab ? 'noopener noreferrer' : undefined}
                      onClick={onClose}
                    >
                      {item.label}
                    </Link>
                  </li>
                )
              })}
            </ul>
          </nav>

          <div className="border-t border-border px-4 py-4 space-y-3">
            {data?.searchEnabled !== false && (
              <Button variant="ghost" className="w-full justify-start" asChild>
                <Link href="/search" onClick={onClose} className="inline-flex items-center gap-2">
                  <SearchIcon className="h-4 w-4" aria-hidden="true" />
                  Search
                </Link>
              </Button>
            )}

            {data?.loginButton?.enabled && (
              <Button variant="ghost" className="w-full justify-start" asChild>
                <Link href={data.loginButton.url || '/t/login'} onClick={onClose}>
                  {data.loginButton.label || 'Login'}
                </Link>
              </Button>
            )}

            {data?.registerButton?.enabled && (
              <Button variant="ghost" className="w-full justify-start" asChild>
                <Link href={data.registerButton.url || '/t/register'} onClick={onClose}>
                  {data.registerButton.label || 'Register'}
                </Link>
              </Button>
            )}

            {data?.contactButton?.enabled && (
              <Button className="w-full bg-brand-red text-white hover:bg-brand-red/90" asChild>
                <Link
                  href={data.contactButton.url || '/contact'}
                  onClick={onClose}
                  className="inline-flex items-center justify-center gap-1"
                >
                  {data.contactButton.label || 'Contact Us'}
                  <ChevronRight className="h-4 w-4" aria-hidden="true" />
                </Link>
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
